/**
 * Settings' Bank Connections (issue #50, stage D): the business connects its
 * own Mercury account, maps Mercury's accounts to the chart, and replaces a
 * token, without anybody running a command.
 *
 * ONE TOKEN, NOT A KEY AND A SECRET. Mercury issues a single API token that
 * begins `secret-token:`, and the form asks for that, in a password field the
 * browser will not autofill or remember. It is sent once, tried against
 * Mercury before anything is stored, stored encrypted, and never shown again:
 * no read returns it (`services/mercury-connections.ts`).
 *
 * THE SECOND FACTOR IS ASKED FOR, as Signing In asks for it, because pointing
 * a business's bank feed at an account is a change a borrowed laptop must not
 * be able to make.
 *
 * MAPPING IS WHERE THE FEED STARTS. A connection with nothing mapped imports
 * nothing: each Mercury account must be told which bank account in the chart
 * it is. So connecting opens the mapping at once, and a connection with none
 * mapped says so.
 *
 * DISCONNECTING DELETES THE TOKEN (migration 0064, the Data Retention and
 * Deletion Policy §3). Syncing stops; the mappings and every imported
 * transaction stay. Reconnecting takes a new token, proved as connecting is,
 * and puts it on the SAME connection, so nothing is imported twice (F-1).
 * Disconnecting asks only for a confirmation, in the Matching screen's inline
 * form: it removes access rather than granting it.
 *
 * PLAID (C-19, migration 0076) sits beside Mercury here: Connect A Bank
 * Through Plaid opens a consent step before any of Plaid's code loads
 * (`PlaidConnect.tsx`). A Plaid connection is mapped with each account's
 * import start, disconnected with its Item removed at Plaid, and re-authorised
 * in Plaid's update mode when its bank asks (Reconnect Needed). A disconnected
 * Plaid connection is never reconnected -- its Item is gone at Plaid -- so its
 * row offers Connect It Again, a fresh connection (review PL-7).
 *
 * Read on its own, as Business Details is, so a failure here leaves the rest
 * of Settings working and is said in this section alone.
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type BankConnectionSummary, type BankMapping, type PlaidMapping } from '../api.js';
import { PlaidConnectFlow, PlaidMappingEditor, PlaidReauthorise } from './PlaidConnect.js';
import { formatTimestamp } from '../lib/timezone.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

type Step =
  | { kind: 'idle' }
  | { kind: 'connect' }
  | { kind: 'map'; id: string }
  | { kind: 'token'; id: string }
  | { kind: 'disconnect'; id: string }
  | { kind: 'reconnect'; id: string }
  | { kind: 'plaid-connect' }
  | { kind: 'plaid-map'; id: string }
  | { kind: 'plaid-reauth'; id: string };

const EMPTY_CONNECT = { displayName: 'Mercury', token: '', syncStartDate: '', code: '' };

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError && caught.message !== caught.code ? caught.message : fallback;
}

export function BankConnectionsSection() {
  const [connections, setConnections] = useState<BankConnectionSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [connect, setConnect] = useState(EMPTY_CONNECT);
  const [mapping, setMapping] = useState<BankMapping | null>(null);
  const [replacement, setReplacement] = useState({ token: '', code: '' });
  const [plaidOn, setPlaidOn] = useState(false);
  const [plaidMapping, setPlaidMapping] = useState<PlaidMapping | null>(null);

  async function load() {
    try {
      setConnections((await api.listBankConnections()).connections);
      setLoadError(null);
    } catch (caught) {
      setLoadError(message(caught, 'Could not read the bank connections.'));
    }
  }

  useEffect(() => {
    void load();
    // Whether Plaid is switched on here; off, its button is not offered.
    api
      .plaidStatus()
      .then((status) => setPlaidOn(status.configured))
      .catch(() => setPlaidOn(false));
  }, []);

  async function openPlaidMapping(id: string) {
    setStep({ kind: 'plaid-map', id });
    setPlaidMapping(null);
    setError(null);
    try {
      setPlaidMapping(await api.readPlaidMapping(id));
    } catch (caught) {
      setError(message(caught, 'Could not read the accounts Plaid lists.'));
    }
  }

  function reset() {
    setStep({ kind: 'idle' });
    setConnect(EMPTY_CONNECT);
    setReplacement({ token: '', code: '' });
    setMapping(null);
    setPlaidMapping(null);
    setError(null);
  }

  async function openMapping(id: string) {
    setStep({ kind: 'map', id });
    setMapping(null);
    setError(null);
    try {
      setMapping(await api.readBankMapping(id));
    } catch (caught) {
      setError(message(caught, 'Could not read the accounts Mercury lists.'));
    }
  }

  async function submitConnect() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { connectionId } = await api.connectMercury({
        ...connect,
        displayName: connect.displayName.trim(),
        token: connect.token.trim(),
      });
      setConnect(EMPTY_CONNECT);
      setNotice('Connected. Now tell it which account is which.');
      await load();
      await openMapping(connectionId);
    } catch (caught) {
      setError(message(caught, 'Could not connect Mercury.'));
    } finally {
      setBusy(false);
    }
  }

  async function changeMapping(connectionId: string, mercuryAccountId: string, accountId: string | null) {
    setError(null);
    try {
      await api.setBankMapping(connectionId, mercuryAccountId, accountId);
      setMapping(await api.readBankMapping(connectionId));
      await load();
    } catch (caught) {
      setError(message(caught, 'Could not change that mapping.'));
    }
  }

  async function submitReplacement(connectionId: string, reconnecting: boolean) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { missing } = reconnecting
        ? await api.reconnectBank(connectionId, replacement.token.trim(), replacement.code)
        : await api.replaceBankToken(connectionId, replacement.token.trim(), replacement.code);
      const done = reconnecting ? 'Reconnected' : 'Token replaced';
      reset();
      setNotice(
        missing.length === 0
          ? done
          : `${done}. It does not see ${missing.length} mapped ${missing.length === 1 ? 'account' : 'accounts'}, which will not sync until it does.`,
      );
      if (reconnecting) await load();
    } catch (caught) {
      setError(message(caught, reconnecting ? 'Could not reconnect.' : 'Could not replace the token.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitDisconnect(connectionId: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.disconnectBank(connectionId);
      const plaid = connections?.find((c) => c.id === connectionId)?.provider === 'plaid';
      reset();
      setNotice(plaid ? 'Disconnected. It is removed at Plaid and its token is deleted.' : 'Disconnected. Its token is deleted.');
      await load();
    } catch (caught) {
      setError(message(caught, 'Could not disconnect.'));
    } finally {
      setBusy(false);
    }
  }

  const connectField = (key: keyof typeof EMPTY_CONNECT, label: string, extra: { type?: string; autoComplete?: string } = {}) => (
    <label className="field">
      <span>{label}</span>
      <input
        type={extra.type ?? 'text'}
        autoComplete={extra.autoComplete ?? 'off'}
        spellCheck={false}
        value={connect[key]}
        onChange={(event) => {
          const value = event.target.value;
          setConnect((current) => ({ ...current, [key]: value }));
        }}
      />
    </label>
  );

  return (
    <section className="settings-section" aria-labelledby="settings-banks">
      <h2 id="settings-banks">Bank Connections</h2>
      <p className="muted">
        Where the bank feed comes from, through Mercury or through Plaid. Each account the bank reports must be mapped
        to a bank account in the chart before its transactions arrive.{' '}
        <InfoNote label="Making A Mercury Token">
          In Mercury, open Settings, then API Tokens, and create a token with read-only access; this application only
          ever reads. Copy the whole token, including secret-token: at the start. Mercury may ask for the addresses the
          token will be used from: this application calls Mercury from its server, not from your browser. The token is
          checked with Mercury before it is saved, stored encrypted, and never shown again. Mercury deletes a token that
          goes unused for 45 days, which the Sync screen warns about.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {connections === null ? (
        !loadError && <p className="muted">Loading…</p>
      ) : connections.length === 0 ? (
        <p className="muted">No bank is connected to this business.</p>
      ) : (
        <ul className="bank-connections">
          {connections.map((connection) => (
            <li key={connection.id}>
              <strong>{connection.displayName}</strong>
              <span className="muted">
                {' '}
                — {connection.provider === 'plaid' ? 'Plaid' : 'Mercury'}
                {connection.environment === 'sandbox' ? ' sandbox' : ''}, connected{' '}
                {formatTimestamp(connection.connectedAt)}, books from {connection.syncStartDate}
                {connection.disabled ? ', disconnected' : ''}.
              </span>{' '}
              <span className={connection.mappedAccounts === 0 ? 'error' : 'muted'}>
                {connection.mappedAccounts === 0
                  ? 'No account is mapped, so nothing is imported.'
                  : `${connection.mappedAccounts} ${connection.mappedAccounts === 1 ? 'account' : 'accounts'} mapped.`}
              </span>
              {connection.reconnectNeeded && (
                <span className="error"> Reconnect Needed: the bank asks you to sign in to it again.</span>
              )}
              {connection.provider === 'plaid' && !connection.disabled && step.kind === 'idle' && (
                <span className="row">
                  {/* Named for its connection: a Mercury row beside it has a Map Accounts of its own. */}
                  <button
                    type="button"
                    className="link"
                    aria-label={`Map Accounts, ${connection.displayName}`}
                    onClick={() => void openPlaidMapping(connection.id)}
                  >
                    Map Accounts
                  </button>
                  {connection.reconnectNeeded && (
                    <button
                      type="button"
                      className="link"
                      onClick={() => {
                        reset();
                        setStep({ kind: 'plaid-reauth', id: connection.id });
                      }}
                    >
                      Re-Authorise
                    </button>
                  )}
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      reset();
                      setStep({ kind: 'disconnect', id: connection.id });
                    }}
                  >
                    Disconnect
                  </button>
                </span>
              )}
              {connection.provider === 'plaid' && connection.disabled && step.kind === 'idle' && plaidOn && (
                <span className="row">
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      reset();
                      setStep({ kind: 'plaid-connect' });
                    }}
                  >
                    Connect It Again
                  </button>
                </span>
              )}
              {step.kind === 'plaid-reauth' && step.id === connection.id && (
                <PlaidReauthorise
                  connectionId={connection.id}
                  displayName={connection.displayName}
                  onCancel={reset}
                  onDone={() => {
                    reset();
                    setNotice('Re-authorised. The next sync carries on where it stopped.');
                    void load();
                  }}
                />
              )}
              {connection.provider === 'mercury' && !connection.disabled && step.kind === 'idle' && (
                <span className="row">
                  <button type="button" className="link" onClick={() => void openMapping(connection.id)}>
                    Map Accounts
                  </button>
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      reset();
                      setStep({ kind: 'token', id: connection.id });
                    }}
                  >
                    Replace Token
                  </button>
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      reset();
                      setStep({ kind: 'disconnect', id: connection.id });
                    }}
                  >
                    Disconnect
                  </button>
                </span>
              )}
              {connection.provider === 'mercury' && connection.disabled && step.kind === 'idle' && (
                <span className="row">
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      reset();
                      setStep({ kind: 'reconnect', id: connection.id });
                    }}
                  >
                    Reconnect
                  </button>
                </span>
              )}
              {step.kind === 'disconnect' && step.id === connection.id && (
                <div className="undo-form" role="group" aria-label={`Disconnect ${connection.displayName}`}>
                  <p>
                    {connection.provider === 'plaid'
                      ? 'The connection is removed at Plaid and its token deleted here, and syncing stops. Every imported transaction stays. Connecting this bank again later starts a new connection, from the day after the last transaction already imported.'
                      : 'Syncing stops and the stored token is deleted. The mapped accounts and every imported transaction stay, and you can reconnect with a new token from Mercury.'}
                  </p>
                  <div className="row">
                    <button type="button" disabled={busy} onClick={() => void submitDisconnect(connection.id)}>
                      {busy ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                    <button type="button" className="link" onClick={reset}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {connections !== null && step.kind === 'idle' && (
        <div className="row">
          <button type="button" disabled={busy} onClick={() => { reset(); setStep({ kind: 'connect' }); }}>
            Connect Mercury
          </button>
          {plaidOn && (
            <button type="button" disabled={busy} onClick={() => { reset(); setStep({ kind: 'plaid-connect' }); }}>
              Connect A Bank Through Plaid
            </button>
          )}
        </div>
      )}

      {step.kind === 'connect' && (
        <div className="new-account undo-form authenticator-form" role="group" aria-label="Connect Mercury">
          <p>Nothing is saved unless Mercury accepts the token.</p>
          {connectField('displayName', 'Connection Name')}
          {connectField('token', 'Mercury API Token', { type: 'password', autoComplete: 'off' })}
          {connectField('syncStartDate', 'Books Begin', { type: 'date' })}
          {connectField('code', 'Your Code Or A Recovery Code', { autoComplete: 'one-time-code' })}
          <div className="row">
            <button type="button" disabled={busy} onClick={() => void submitConnect()}>
              {busy ? 'Checking With Mercury…' : 'Connect'}
            </button>
            <button type="button" className="link" onClick={reset}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step.kind === 'map' && (
        <div className="new-account" role="group" aria-label="Map Accounts">
          <p>Which bank account in the chart each Mercury account is. A card is mapped to the card&apos;s account.</p>
          {mapping === null ? (
            !error && <p className="muted">Asking Mercury…</p>
          ) : mapping.mercury.length === 0 ? (
            <p className="muted">Mercury lists no accounts for this token.</p>
          ) : (
            <table className="lines">
              <thead>
                <tr>
                  <th>Mercury Account</th>
                  <th>Chart Account</th>
                </tr>
              </thead>
              <tbody>
                {mapping.mercury.map((row) => (
                  <tr key={row.mercuryAccountId}>
                    <td>
                      {row.name}
                      {row.kind && <span className="muted"> ({row.kind})</span>}
                    </td>
                    <td>
                      <select
                        aria-label={`Chart account for ${row.name}`}
                        value={row.mappedTo?.accountId ?? ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          void changeMapping(step.id, row.mercuryAccountId, value === '' ? null : value);
                        }}
                      >
                        <option value="">Not Mapped</option>
                        {mapping.chart.map((account) => (
                          <option key={account.accountId} value={account.accountId}>
                            {account.code} {account.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="row">
            <button type="button" className="link" onClick={reset}>
              Done
            </button>
          </div>
        </div>
      )}

      {(step.kind === 'token' || step.kind === 'reconnect') && (
        <div
          className="new-account undo-form authenticator-form"
          role="group"
          aria-label={step.kind === 'reconnect' ? 'Reconnect' : 'Replace Token'}
        >
          <p>
            {step.kind === 'reconnect'
              ? 'A new token turns this connection back on, with its mappings and history. A token from another Mercury organisation is refused.'
              : 'The connection, its mappings and its history stay; only the token changes. A token from another Mercury organisation is refused.'}
          </p>
          <label className="field">
            <span>New Mercury API Token</span>
            <input
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={replacement.token}
              onChange={(event) => {
                const value = event.target.value;
                setReplacement((current) => ({ ...current, token: value }));
              }}
            />
          </label>
          <label className="field">
            <span>Your Code Or A Recovery Code</span>
            <input
              autoComplete="one-time-code"
              value={replacement.code}
              onChange={(event) => {
                const value = event.target.value;
                setReplacement((current) => ({ ...current, code: value }));
              }}
            />
          </label>
          <div className="row">
            <button
              type="button"
              disabled={busy}
              onClick={() => void submitReplacement(step.id, step.kind === 'reconnect')}
            >
              {busy ? 'Checking With Mercury…' : step.kind === 'reconnect' ? 'Reconnect It' : 'Replace It'}
            </button>
            <button type="button" className="link" onClick={reset}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {step.kind === 'plaid-connect' && (
        <PlaidConnectFlow
          onCancel={reset}
          onConnected={(connectionId) => {
            setNotice('Connected through Plaid. Now choose which chart account each of its accounts feeds.');
            void load();
            void openPlaidMapping(connectionId);
          }}
        />
      )}

      {step.kind === 'plaid-map' && plaidMapping && (
        <PlaidMappingEditor
          connectionId={step.id}
          mapping={plaidMapping}
          onChanged={() => {
            void api.readPlaidMapping(step.id).then(setPlaidMapping);
            void load();
          }}
          onDone={reset}
        />
      )}
    </section>
  );
}
